import { useEffect } from "react";

const SITE = "https://realtyvaultmgr.com";
const DEFAULT_OG = `${SITE}/marketing/og-share.jpg`;
const SITE_NAME = "RealtyVault Manager";
const MANAGED_ATTR = "data-seo-managed";

/**
 * Per-page SEO component.
 *
 * Directly manipulates <head> tags (title, meta description, canonical,
 * Open Graph, Twitter, JSON-LD) on mount so Googlebot and social scrapers
 * see the right metadata for each SPA route.
 *
 * Why not react-helmet-async? Helmet v3 appends tags without removing the
 * static fallbacks in index.html, resulting in duplicate/conflicting meta
 * tags. This direct-DOM approach guarantees exactly one authoritative tag
 * per key at any time — matches Google's "one description tag" guideline.
 *
 * Static tags in index.html remain as JS-disabled fallbacks for LinkedIn,
 * Slack, and Discord crawlers that don't execute JavaScript.
 */
export default function SEO({
  title,
  description,
  path = "/",
  image = DEFAULT_OG,
  type = "website",
  noindex = false,
  jsonLd = null,
  article = null,
}) {
  useEffect(() => {
    const fullTitle = title
      ? `${title} — ${SITE_NAME}`
      : `${SITE_NAME} — Commercial Real-Estate Portfolio Management Software`;
    const canonical = `${SITE}${path.startsWith("/") ? path : `/${path}`}`;
    const robots = noindex
      ? "noindex, nofollow"
      : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";

    document.title = fullTitle;

    setMeta("name", "description", description || "");
    setMeta("name", "robots", robots);
    setMeta("name", "googlebot", robots);
    setCanonical(canonical);

    // Open Graph
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", description || "");
    setMeta("property", "og:url", canonical);
    setMeta("property", "og:type", type);
    setMeta("property", "og:image", image);
    setMeta("property", "og:site_name", SITE_NAME);
    setMeta("property", "og:locale", "en_US");

    // Article-only OG
    if (type === "article") {
      if (article?.publishedTime) setMeta("property", "article:published_time", article.publishedTime);
      if (article?.modifiedTime) setMeta("property", "article:modified_time", article.modifiedTime);
      if (article?.author) setMeta("property", "article:author", article.author);
      removeManaged('meta[property="article:tag"]');
      (article?.tags || []).forEach((t) => appendManagedMeta("property", "article:tag", t));
    } else {
      removeManaged('meta[property^="article:"]');
    }

    // Twitter
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:site", "@realtyvaultmgr");
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", description || "");
    setMeta("name", "twitter:image", image);

    // JSON-LD — remove ALL existing (including static from index.html) then add fresh.
    // Each page owns its own structured data; leaking a stale FAQPage or
    // SoftwareApplication schema onto a non-homepage route is a policy risk.
    document.querySelectorAll('script[type="application/ld+json"]').forEach((el) => el.remove());
    const jsonLdArray = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
    jsonLdArray.forEach((obj) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute(MANAGED_ATTR, "1");
      s.textContent = JSON.stringify(obj);
      document.head.appendChild(s);
    });

    return () => {
      // Clean up JSON-LD when unmounting (title/meta will be overwritten
      // by the next route's SEO component)
      removeManaged('script[type="application/ld+json"]');
    };
  }, [title, description, path, image, type, noindex, JSON.stringify(jsonLd), JSON.stringify(article)]);

  return null;
}

/**
 * Upsert a <meta {attr}="{key}"> tag. If a static (non-managed) tag with
 * the same key exists, we OVERWRITE its content and take ownership so we
 * never end up with duplicates.
 */
function setMeta(attr, key, content) {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  el.setAttribute(MANAGED_ATTR, "1");
}

function setCanonical(href) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  el.setAttribute(MANAGED_ATTR, "1");
}

function appendManagedMeta(attr, key, content) {
  const el = document.createElement("meta");
  el.setAttribute(attr, key);
  el.setAttribute("content", content);
  el.setAttribute(MANAGED_ATTR, "1");
  document.head.appendChild(el);
}

function removeManaged(selector) {
  document.querySelectorAll(selector).forEach((el) => {
    if (el.hasAttribute(MANAGED_ATTR)) el.remove();
  });
}
